<template>
    <div class="forecast-invoice-wrapper" ref="invoiceTable">
        <CreateInvoiceModal v-if="showingCreateInvoice" @cancel="showingCreateInvoice = false"
            @create="createInvoice" />
        <EditInvoiceModal v-if="showingEditInvoice" :invoice="selectedInvoice" @cancel="showingEditInvoice = false"
            @update="updateInvoice" />
        <div class="forecast-invoice-wrapper-head">
            <div class="forecast-invoice-wrapper-head-title">
                {{ $t("billing.myInvoices") }}
            </div>
            <div class="forecast-invoice-wrapper-head-actions">
                <a-select v-model="filters.type" style="width: 220px" dropdownClassName="custom-select-dropdown">
                    <a-select-option v-for="(option, index) in invoiceOptions" :key="index" :value="option.value">
                        {{ option.label }}
                    </a-select-option>
                </a-select>
                <SupsisButton v-if="isAdmin" @click="showingCreateInvoice = true">{{ $t("billing.createInvoice") }}
                </SupsisButton>
            </div>
        </div>
        <div class="forecast-invoice-wrapper-content">
            <supsis-table @cursor-changed="cursorChanged" :paginationCursor="paginationCursor"
                :columns="getInvoiceTableColumns()" :rows="allInvoices" :bottomPagination="true" :zebra-row="false" />
        </div>
    </div>
</template>
<script>
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import {
    createInvoice,
    forceDeleteInvoice,
    getInvoices,
    markInvoiceAsPaid,
    refundInvoice,
    takePayment,
    updateInvoiceById,
} from "../../../../api/billing";
import SupsisTable from "../../../../components/SupsisTable.vue";
import PlanItemButton from "./PlanItemButton.vue";
import MoneyFormatter from "../../../../components/MoneyFormatter.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import CreateInvoiceModal from "../../../../components/billing/CreateInvoiceModal.vue";
import EditInvoiceModal from "../../../../components/billing/EditInvoiceModal.vue";
import MinimalPopoverSelect from "../../../../components/popup-chat/chat-window/MinimalPopoverSelect.vue";
import _ from "lodash";

export default {
    name: "ForecastInvoice",
    components: {
        CreateInvoiceModal,
        EditInvoiceModal,
        SupsisButton,
        SupsisTable,
        PlanItemButton,
        MoneyFormatter,
        MinimalPopoverSelect,
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            currentPlans: (state) => state.site.currentPlans,
            plans: (state) => state.site.plans,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        ...mapGetters({
            isSavedCreditCard: "site/isSavedCreditCard",
        }),
        allInvoices() {
            const nextMonth = this.$moment().startOf("month");
            return [
                this.totalForecastPrice &&
                ["monthly", "unpaid", "all"].includes(this.filters.type) && {
                    _id: (nextMonth.valueOf() / 1000).toString(16).slice(0, 8),
                    invalidPayment: false,
                    iyzicoId: null,
                    price: this.totalForecastPrice,
                    createdDate: nextMonth,
                    pdf: null,
                    type: "forecast",
                },
                ...this.invoices,
            ].filter(Boolean);
        },
    },
    data() {
        return {
            showingCreateInvoice: false,
            showingEditInvoice: false,
            selectedInvoice: null,
            totalForecastPrice: null,
            invoices: [],
            filters: {
                type: "all",
            },
            paginationCursor: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            invoiceOptions: [],
            invoiceFocus: this.$route.query.focus === "invoice",
            // API çağrıları için loading state'leri
            refundInvoiceLoading: new Set(), // Birden fazla fatura için ayrı ayrı takip
            getPaymentLoading: new Set(), // Birden fazla fatura için ayrı ayrı takip
        }
    },
    methods: {
        ...mapMutations({
            CHANGE_SUSPEND_STATUS: "site/CHANGE_SUSPEND_STATUS",
        }),
        ...mapActions({
            getRunningInvoice: "site/getRunningInvoice",
        }),
        focusInvoice() {
            this.$nextTick(() => {
                const invoiceElement = this.$refs.invoiceTable;
                if (invoiceElement) {
                    invoiceElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => invoiceElement.classList.add("focused"), 0);
                    this.invoiceFocus = false;
                    this.$router.replace({ query: { ...this.$route.query, focus: undefined } });
                }
            });
        },
        copyToClipboard(text) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    this.$notification.success({
                        message: this.$t("billing.copied"),
                        description: this.$t("billing.copiedToClipboard"),
                        duration: 2,
                    });
                })
                .catch(() => {
                    this.$notification.error({
                        message: this.$t("billing.error"),
                        description: this.$t("billing.copyFailed"),
                        duration: 2,
                    });
                });
        },
        cursorChanged({ page, perPage }) {
            this.paginationCursor.page = page;
            this.paginationCursor.perPage = perPage;
            this.fetchInvoices();
        },
        fetchInvoices: _.debounce(function (type, changeState = true) {
            const isShowingForecast = ["monthly", "unpaid", "all"].includes(this.filters.type);
            return getInvoices(
                { ...this.filters, type: type || this.filters.type },
                {
                    page: this.paginationCursor.page,
                    perPage: isShowingForecast ? this.paginationCursor.perPage - 1 : this.paginationCursor.perPage,
                }
            ).then((response) => {
                if (changeState) {
                    this.invoices = response.data.invoices;
                    this.paginationCursor.total = response.data.total;
                }
                return response.data;
            });
        }, 400),
        getInvoiceTableColumns() {
            return [
                this.isAdmin && {
                    title: "",
                    field: "isInvoiceGenerated",
                    key: "isInvoiceGenerated",
                    width: "50px",
                    renderBodyCell: ({ row }) => {
                        const isGenerated = row.isInvoiceGenerated === true;
                        const hasTableLink = isGenerated && row.recordId;
                        const openInvoiceTableRecord = () => {
                            if (!hasTableLink) {
                                return;
                            }
                            window.open(
                                "https://supsis.live/tables/t/67b49dbed98fda5f82e89512?recordId=" + row.recordId,
                                "_blank"
                            );
                        };
                        return (
                            <a-tooltip title={isGenerated ? this.$t("billing.invoiceGenerated") : this.$t("billing.invoiceNotGenerated")} placement="top">
                                <div
                                    onClick={openInvoiceTableRecord}
                                    style={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        backgroundColor: isGenerated ? "#52c41a" : "#ff4d4f",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: hasTableLink ? "pointer" : "default",
                                    }}
                                >
                                    <i
                                        class="material-icons"
                                        style={{
                                            fontSize: "14px",
                                            color: "#fff",
                                        }}
                                    >
                                        {isGenerated ? "check" : "close"}
                                    </i>
                                </div>
                            </a-tooltip>
                        );
                    },
                },
                this.isAdmin && {
                    title: this.$t("billing.paymentId"),
                    dataIndex: "iyzicoId",
                    key: "iyzicoId",
                    width: "120px",
                    renderBodyCell: ({ row }) => {
                        const text = row.paymentTransactionId || this.$t("billing.notAvailable");
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.billType"),
                    field: "type",
                    key: "type",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color="#1C1B1F"> {this.formatBillType(row.type)}</a-tag>;
                    },
                },
                {
                    title: this.$t("billing.invoiceId"),
                    field: "_id",
                    key: "_id",
                    width: "120px",
                    renderBodyCell: ({ row }) => {
                        const text = row._id.slice(0, 8);
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.invoiceAmount"),
                    field: "price",
                    key: "price",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = row.type === "forecast" ? row.price + " TL (" + this.$t("billing.estimated") + ")" : row.price + " TL";
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {row.type === "forecast" ? (
                                    <span>
                                        <MoneyFormatter price={row.price} /> ({this.$t("billing.estimated")})
                                    </span>
                                ) : (
                                    <MoneyFormatter price={row.price} />
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.invoiceDate"),
                    field: "createdDate",
                    key: "createdDate",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = this.$moment(row.createdDate).format("YYYY/MM/DD");
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.dueDate"),
                    field: "dueDate",
                    key: "dueDate",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = row.dueDate ? this.$moment(row.dueDate).format("YYYY/MM/DD") : "-";
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("billing.paymentDate"),
                    field: "paymentDate",
                    key: "paymentDate",
                    width: "150px",
                    renderBodyCell: ({ row }) => {
                        const text = row.paymentDate ? this.$moment(row.paymentDate).format("YYYY/MM/DD") : "-";
                        return (
                            <div class="copyable-cell" onClick={() => this.copyToClipboard(text)}>
                                {text}
                            </div>
                        );
                    },
                },
                this.isAdmin && {
                    title: this.$t("billing.invalidBill"),
                    field: "invalidPayment",
                    key: "invalidPayment",
                    width: "120px",
                    renderBodyCell: ({ row }) => {
                        return row.invalidPayment ? (
                            <a-tag color="red">{this.$t("message.yes")}</a-tag>
                        ) : (
                            <a-tag color="green">{this.$t("message.no")}</a-tag>
                        );
                    },
                },
                {
                    title: this.$t("billing.invoiceStatus"),
                    field: "paid",
                    key: "paid",
                    width: "130px",
                    renderBodyCell: ({ row }) => {
                        const PaymentIcon = () => {
                            return (
                                <div
                                    class="payment-icon"
                                    status={
                                        row.refund
                                            ? "refund"
                                            : row.paid
                                                ? "paid"
                                                : row.type === "forecast"
                                                    ? "forecast"
                                                    : "not-paid"
                                    }
                                ></div>
                            );
                        };
                        return (
                            <div class="payment-status">
                                <PaymentIcon />
                                {row.refund
                                    ? this.$t("message.refunded")
                                    : row.paid
                                        ? this.$t("message.paid")
                                        : row.type === "forecast"
                                            ? this.$t("message.pending")
                                            : this.$t("message.unpaid")}
                            </div>
                        );
                    },
                },
                {
                    title: "",
                    field: "pdf",
                    key: "pdf",
                    align: "right",
                    width: "200px",
                    maxWidth: "200px",
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        const downloadPdf = async () => {
                            const pdfUrl = "https://static.supsis.live/" + row.pdf;
                            window.open(pdfUrl, "_blank");
                        };
                        const showInvoice = () => {
                            if (row.type === "forecast") {
                                this.$router.push(`/billing/invoice/forecast`);
                                return;
                            }
                            this.$router.push(`/billing/invoice/${row._id}`);
                        };

                        // Popover için options oluştur
                        const getPopoverOptions = () => {
                            const options = [];

                            // PDF indirme
                            if (row.pdf) {
                                options.push({
                                    value: "downloadPdf",
                                    label: this.$t("message.invoicePdf"),
                                    icon: "description",
                                });
                            }

                            if (!row.paid && row.type !== "forecast") {
                                options.push({
                                    value: "copyPaymentLink",
                                    label: "Ödeme Linki Kopyala",
                                    icon: "link",
                                });
                            }

                            // Admin aksiyonları
                            if (this.isAdmin && row.type !== "forecast") {
                                const adminOptions = [];

                                if (!row.paid) {
                                    adminOptions.push({
                                        value: "markAsPaid",
                                        label: this.$t("billing.markAsPaid"),
                                        icon: "check_circle",
                                    });
                                }

                                if (row.paid && row.paymentTransactionId && !row.refund) {
                                    adminOptions.push({
                                        value: "refundInvoice",
                                        label: this.$t("billing.refundPayment"),
                                        icon: "undo",
                                    });
                                }

                                adminOptions.push({
                                    value: "editInvoice",
                                    label: this.$t("billing.editInvoice"),
                                    icon: "edit",
                                });

                                adminOptions.push({
                                    value: "deleteInvoice",
                                    label: this.$t("billing.deleteInvoice"),
                                    icon: "delete",
                                });

                                // Admin options'ları group ile ekle
                                adminOptions.forEach((opt) => {
                                    options.push({
                                        ...opt,
                                        group: this.$t("billing.adminGroup"),
                                    });
                                });
                            }

                            return options;
                        };

                        const handlePopoverSelect = (value) => {
                            switch (value) {
                                case "downloadPdf":
                                    downloadPdf();
                                    break;
                                case "copyPaymentLink":
                                    this.copyPaymentLink(row);
                                    break;
                                case "markAsPaid":
                                    this.markAsPaid(row._id);
                                    break;
                                case "refundInvoice":
                                    this.refundInvoice(row._id);
                                    break;
                                case "editInvoice":
                                    this.editInvoice(row);
                                    break;
                                case "deleteInvoice":
                                    this.deleteInvoice(row._id);
                                    break;
                            }
                        };

                        const popoverOptions = getPopoverOptions();

                        return (
                            <div class="invoice-actions-cell">
                                {!row.paid && row.type !== "forecast" && (
                                    <PlanItemButton
                                        on-click={() => this.getPayment(row._id)}
                                        disabled={this.getPaymentLoading.has(row._id)}
                                        loading={this.getPaymentLoading.has(row._id)}
                                    >
                                        {this.$t("message.makePayment")}
                                    </PlanItemButton>
                                )}
                                <PlanItemButton on-click={showInvoice}>
                                    {this.$t("message.viewInvoice")}
                                </PlanItemButton>
                                {popoverOptions.length > 0 &&
                                    this.$createElement(MinimalPopoverSelect, {
                                        props: {
                                            value: "",
                                            options: popoverOptions,
                                            showDropdownArrow: false,
                                            showIcon: false,
                                            multiple: false,
                                            triggerClass: "invoice-more-trigger",
                                            buttonClass: "invoice-more-button",
                                            popoverClass: "invoice-more-popover",
                                            placement: "topRight",
                                            hideSelection: true,
                                        },
                                        on: {
                                            change: handlePopoverSelect,
                                        },
                                        scopedSlots: {
                                            trigger: () =>
                                                this.$createElement(PlanItemButton, [
                                                    this.$createElement("i", {
                                                        class: "material-icons",
                                                        style: { fontSize: "18px" },
                                                    }, "more_horiz"),
                                                ]),
                                        },
                                    })}
                            </div>
                        );
                    },
                },
            ].filter(Boolean);
        },
        formatBillType(type) {
            const types = {
                none: this.$t("billing.billTypeNone"),
                manuel: this.$t("billing.billTypeManual"),
                monthly: this.$t("billing.monthly"),
                "monthly-3": this.$t("billing.billTypeMonthly3"),
                "monthly-6": this.$t("billing.billTypeMonthly6"),
                annual: this.$t("billing.billTypeAnnual"),
                "annual-2": this.$t("billing.billTypeAnnual2"),
                education: this.$t("billing.billTypeEducation"),
                consultancy: this.$t("billing.billTypeConsultancy"),
                installation: this.$t("billing.billTypeInstallation"),
                other: this.$t("billing.billTypeOther"),
                "quick-purchase": this.$t("message.packagePurchase"),
                "wallet-deposit": this.$t("billing.balanceTopUp"),
                forecast: this.$t("message.upcomingInvoice"),
            };
            return types[type] || type;
        },
        /**
         * Fatura ödemesi yapar
         * API çağrısı tamamlanana kadar buton disabled kalır
         * @param {string} invoiceId - Ödeme yapılacak fatura ID'si
         */
        async getPayment(invoiceId) {
            // Eğer zaten bu fatura için işlem devam ediyorsa, tekrar tıklamayı engelle
            if (this.getPaymentLoading.has(invoiceId)) {
                return;
            }

            if (!this.isSavedCreditCard) {
                this.$notification.error({
                    message: this.$t("billing.paymentNotPossible"),
                    description: this.$t("billing.saveCardFirst"),
                });
                return;
            }

            // Loading state'ini aktif et
            this.getPaymentLoading.add(invoiceId);

            try {
                const response = await takePayment(invoiceId)
                    .then((res) => res.data)
                    .catch((e) => {
                        return { status: "error", message: null };
                    });

                if (response.status === "error") {
                    this.$notification.error({
                        message: this.$t("message.paymentFailed"),
                        description: response.message || this.$t("message.paymentProcessError"),
                    });
                    return;
                }

                const isSuspended = this.site.suspend;
                if (isSuspended) {
                    const unpaidInvoices = await this.fetchInvoices("unpaid", false).then((data) => data.invoices);
                    const passedDueDateInvoices = unpaidInvoices.filter((invoice) => {
                        const dueDate = this.$moment(invoice.dueDate);
                        return dueDate.isBefore(this.$moment());
                    });
                    if (passedDueDateInvoices.length <= 0) {
                        this.filters.type = "all";
                        this.CHANGE_SUSPEND_STATUS(false);
                    }
                }

                // Faturaları yeniden çek ve fatura statusünün güncellenmesini bekle
                await this.fetchInvoices();

                this.$notification.success({
                    message: this.$t("message.paymentSuccessful"),
                    description: this.$t("message.paymentProcessSuccessful"),
                });
            } finally {
                // API çağrısı tamamlandığında (başarılı veya hatalı) loading state'ini kaldır
                this.getPaymentLoading.delete(invoiceId);
            }
        },
        async deleteInvoice(invoiceId) {
            await this.$dialog.confirm(this.$t("message.deleteConfirm", { object: this.$t("billing.invoice") }), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            await forceDeleteInvoice(this.site._id, invoiceId);
            this.fetchInvoices();
        },
        /**
         * Fatura geri ödemesi yapar
         * API çağrısı tamamlanana kadar buton disabled kalır
         * @param {string} invoiceId - Geri ödeme yapılacak fatura ID'si
         */
        async refundInvoice(invoiceId) {
            // Eğer zaten bu fatura için işlem devam ediyorsa, tekrar tıklamayı engelle
            if (this.refundInvoiceLoading.has(invoiceId)) {
                return;
            }

            try {
                await this.$dialog.confirm(this.$t("billing.areYouSoreRefundInvoice"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                });
            } catch {
                // Kullanıcı iptal ettiyse işlemi durdur
                return;
            }

            // Loading state'ini aktif et
            this.refundInvoiceLoading.add(invoiceId);

            try {
                await refundInvoice(invoiceId);
                this.$notification.success({
                    message: this.$t("message.invoiceRefund"),
                    description: this.$t("message.invoiceRefundedSuccessfully"),
                });
                await this.fetchInvoices();
            } finally {
                // API çağrısı tamamlandığında (başarılı veya hatalı) loading state'ini kaldır
                this.refundInvoiceLoading.delete(invoiceId);
            }
        },
        async markAsPaid(invoiceId) {
            await this.$dialog.confirm(this.$t("billing.areYouSurePaid"), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            const isSuccess = await markInvoiceAsPaid(this.site._id, invoiceId)
                .then((res) => res.data)
                .catch(() => false);
            if (!isSuccess) {
                this.$notification.error({
                    message: this.$t("message.invoicePaymentError"),
                    description: this.$t("message.invoicePaymentProcessError"),
                });
                return;
            }
            this.$notification.success({
                message: this.$t("message.invoicePaid"),
                description: this.$t("message.invoiceMarkedAsPaid"),
            });
            this.fetchInvoices();
        },
        copyPaymentLink(row) {
            var siteId = this.site._id;
            var t;
            if (row.paymentToken) {
                t = row.paymentToken;
            } else {
                var payload = { s: siteId, a: true };
                if (row._id) payload.i = row._id;
                t = btoa(JSON.stringify(payload));
            }
            var link = "https://payment.supsis.ai/" + encodeURIComponent(t);
            this.copyToClipboard(link);
        },
        editInvoice(invoice) {
            this.selectedInvoice = invoice;
            this.showingEditInvoice = true;
        },
        async updateInvoice(invoiceId, updateData) {
            const success = await updateInvoiceById(invoiceId, updateData)
                .then(() => true)
                .catch(() => false);

            if (!success) {
                this.$notification.error({
                    message: this.$t("message.invoiceUpdateError"),
                    description: this.$t("auto.invoiceUpdateFailedDescription"),
                });
                return;
            }

            this.$notification.success({
                message: this.$t("message.invoiceUpdated"),
                description: this.$t("auto.invoiceUpdatedSuccessfullyDescription"),
            });

            this.showingEditInvoice = false;
            this.fetchInvoices();
        },
        async createInvoice(price, description, createdDate, dueDate, paid, type, paymentDate, installment) {
            const invoice = createInvoice(price, description, createdDate, {
                dueDate,
                paid,
                type,
                paymentDate,
                installment: installment || 1,
            })
                .then((res) => res.data)
                .catch(() => null);
            if (!invoice) {
                this.$toasted.error(this.$t("billing.createdNewInvoiceFailed"), {
                    duration: 3000,
                    position: "top-center",
                });
                return;
            }
            this.$toasted.success(this.$t("billing.createdNewInvoice"), {
                duration: 3000,
                position: "top-center",
            });
            this.showingCreateInvoice = false;
            this.fetchInvoices();
        },
    },
    mounted() {
        if (this.invoiceFocus) {
            this.focusInvoice();
        }
        const isSuspend = this.site.suspend;
        if (isSuspend) this.filters.type = "unpaid";
        this.getRunningInvoice(true).then((runningInvoice) => {
            this.totalForecastPrice = runningInvoice.endOfMonthForecastPrice;
        });
        this.invoiceOptions = [
            { value: "all", label: "billing.allInvoices" },
            { value: "manual", label: "billing.manualInvoices" },
            { value: "monthly", label: "billing.endOfPeriodInvoices" },
            { value: "purchases", label: "billing.purchaseInvoices" },
            { value: "wallet-deposit", label: "billing.walletDeposit" },
            { value: "unpaid", label: "billing.unpaidInvoices" },
            { value: "paid", label: "billing.paidInvoices" },
        ].map((option) => ({
            value: option.value,
            label: this.$t(option.label),
        }));
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                this.fetchInvoices();
            },
        },
    },
};
</script>
<style lang="scss" scoped>
@keyframes focus-border {
    0% {
        border: 2px solid transparent;
        border-radius: 8px;
    }
    50% {
        border: 2px solid #0288d1;
        border-radius: 8px;
    }
    100% {
        border: 2px solid transparent;
        border-radius: 8px;
    }
}

.forecast-invoice-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;

    &.focused {
        animation: focus-border 1s ease-in-out 1s;
    }

    .copyable-cell {
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(2, 136, 209, 0.1);
        }

        &:active {
            background-color: rgba(2, 136, 209, 0.2);
        }
    }

    &-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        &-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 5px;
            font-size: 20px;
            font-weight: 600;
            color: #1c1b1f;

            svg {
                width: 20px;
                height: 20px;
            }
        }
    }

    &-content {
        width: 100%;
        background: #f1f4f7;
        border-radius: 8px;
        box-sizing: border-box;
        padding: 10px;

        .supsis-table {
            width: 100%;
        }

        .payment-status {
            display: flex;
            align-items: center;
            gap: 5px;

            .payment-icon {
                width: 10px;
                height: 10px;
                border-radius: 100px;

                &[status="paid"] {
                    background-color: #2ecc71;
                }

                &[status="refund"] {
                    background-color: #e74c3c;
                }

                &[status="forecast"] {
                    background-color: #f1c40f;
                }

                &[status="not-paid"] {
                    background-color: #e74c3c;
                }
            }
        }

        // Fixed kolonlar için maksimum genişlik kontrolü
        :deep(.supsis-col-fixed) {
            max-width: 200px !important;
            overflow: hidden;
        }

        .invoice-actions-cell {
            display: flex;
            align-items: center;
            gap: 5px;
            justify-content: flex-end;
            width: 100%;

            .modern-select-trigger {
                width: auto;
            }

            > * {
                flex-shrink: 0;
            }
        }

        // Actions kolonu için sağa hizalama
        :deep(.supsis-col-pdf) {
            .supsis-header-cell,
            .supsis-body-cell {
                display: flex;
                justify-content: flex-end;
            }
        }

        .invoice-more-trigger {
            display: inline-flex;
        }

        .invoice-more-button {
            padding: 0;
            border: none;
            background: transparent;
            min-width: auto;
            width: auto;

            &:hover {
                background: transparent;
                border: none;
            }
        }
    }
}
</style>
